import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  ShieldCheck,
  Lock,
  Zap,
  Building2,
  ArrowRight,
  Sparkles,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import OnboardingShell from '../components/OnboardingShell';

const API = process.env.REACT_APP_BACKEND_URL;

const TRUST_POINTS = [
  {
    icon: Lock,
    title: 'NoFee never holds your money',
    desc: 'Every payment flows directly through Stripe to your own bank account. NoFee has no access to customer funds.',
  },
  {
    icon: Building2,
    title: 'You own your Stripe account',
    desc: 'The account belongs to your restaurant, not NoFee. You can review payouts, refund customers and download reports anytime in Stripe.',
  },
  {
    icon: Zap,
    title: 'Fast, predictable payouts',
    desc: 'Stripe sends money to your bank on a daily or weekly schedule that you control — no waiting weeks for a third-party platform.',
  },
  {
    icon: ShieldCheck,
    title: 'Trusted by millions of businesses',
    desc: 'Stripe is the same payment infrastructure used by Shopify, Amazon, Lyft, Substack and 5+ million businesses worldwide.',
  },
];

/**
 * Step 2 of restaurant onboarding — Payout Trust.
 *
 * The single most important step psychologically: BEFORE asking the owner to
 * connect a bank or pay anything, explain in plain language how money flows.
 * Non-technical restaurant owners must finish this page thinking:
 *   "I understand exactly how I get paid — and NoFee never touches my money."
 */
const OnboardingPayoutsPage = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [advancing, setAdvancing] = useState(false);

  // Defensive: gate on auth
  useEffect(() => {
    if (!token) navigate('/');
  }, [token, navigate]);

  const onContinue = async () => {
    setAdvancing(true);
    try {
      await axios.post(
        `${API}/api/onboarding/advance`,
        { step: 'stripe_connect' },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      navigate('/onboarding/stripe-connect');
    } catch (e) {
      toast.error('Could not continue. Please try again.');
    } finally {
      setAdvancing(false);
    }
  };

  return (
    <OnboardingShell
      currentStep="payout_trust"
      backFallback="/dashboard"
      testid="onboarding-payouts-page"
    >
        <div>
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-yellow-500/30 bg-yellow-500/[0.08]">
            <ShieldCheck className="w-3.5 h-3.5 text-yellow-300" />
            <span className="text-[10.5px] uppercase tracking-[0.22em] text-yellow-300 font-bold">
              How you get paid
            </span>
          </div>

          <h1
            data-testid="payouts-headline"
            className="mt-5 font-['Outfit',_Inter,_sans-serif] text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-[-0.03em] leading-[1.05] text-white"
          >
            Every dollar a customer pays goes{' '}
            <span className="bg-gradient-to-r from-yellow-300 via-yellow-400 to-amber-500 bg-clip-text text-transparent">
              straight to your bank
            </span>
            .
          </h1>
          <p className="mt-4 text-[15px] sm:text-base text-white/65 leading-relaxed max-w-2xl">
            NoFee uses Stripe — the same payment infrastructure powering Shopify and Amazon — so
            you keep 100% of every order. Here's exactly how it works.
          </p>

          {/* Trust grid */}
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-4">
            {TRUST_POINTS.map(({ icon: Icon, title, desc }) => (
              <div
                key={title}
                data-testid={`trust-point-${title.toLowerCase().replace(/\s+/g, '-')}`}
                className="p-6 rounded-2xl border border-white/8 bg-white/[0.02] hover:border-yellow-500/30 transition-colors"
              >
                <div className="w-11 h-11 rounded-xl bg-yellow-500/10 border border-yellow-500/30 flex items-center justify-center mb-4">
                  <Icon className="w-5 h-5 text-yellow-400" strokeWidth={1.7} />
                </div>
                <h3 className="font-['Outfit',_Inter,_sans-serif] text-[16px] font-semibold tracking-tight text-white">
                  {title}
                </h3>
                <p className="mt-2 text-[13.5px] text-white/55 leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>

          {/* Stripe brand strip */}
          <div className="mt-8 px-5 py-4 rounded-2xl border border-white/8 bg-white/[0.02] flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-3">
              <div className="px-3 py-1.5 rounded-lg bg-[#635BFF]/15 border border-[#635BFF]/30 text-[#A8A4FF] text-[11px] uppercase tracking-[0.2em] font-bold">
                Powered by Stripe
              </div>
              <span className="text-[12.5px] text-white/55">
                Bank-level security · PCI DSS Level 1 certified
              </span>
            </div>
          </div>

          {/* Primary CTA */}
          <div className="mt-10 flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
            <p className="text-[12.5px] text-white/45">
              Setup takes about 3 minutes. You'll need your bank info and business details.
            </p>
            <button
              type="button"
              onClick={onContinue}
              disabled={advancing}
              data-testid="payouts-continue-btn"
              className="group inline-flex items-center justify-center gap-2 px-7 py-4 min-h-[56px] rounded-2xl bg-gradient-to-b from-yellow-300 to-yellow-500 hover:from-yellow-200 hover:to-yellow-400 text-black font-extrabold text-[15px] tracking-tight disabled:opacity-60 disabled:cursor-not-allowed transition-all shadow-[0_18px_50px_-12px_rgba(250,204,21,0.55)] hover:-translate-y-0.5"
            >
              <Sparkles className="w-4 h-4" />
              {advancing ? 'Continuing…' : 'Continue to Stripe setup'}
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
            </button>
          </div>
        </div>
    </OnboardingShell>
  );
};

export default OnboardingPayoutsPage;
